// src/components/ChessCamp.jsx
import React, { useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";

export default function ChessCamp() {
  // === CONSTS ===
  const EVENTBRITE_URL =
    "https://www.eventbrite.co.uk/e/hampshire-junior-open-chess-congress-2025-tickets-1117790594759?aff=erelexpmlt"; // <-- Twój link do turnieju

  // Keep the SAME Stripe Buy Button IDs and publishable key you provided
  const dayBtnRef = useRef(null);
  const weekBtnRef = useRef(null);

  // --- Load Stripe Buy Button script and mount custom elements when ready
  useEffect(() => {
    const SCRIPT_ID = "stripe-buy-button-script";

    const ensureScript = () =>
      new Promise((resolve) => {
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
          resolve();
          return;
        }
        const s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.async = true;
        s.src = "https://js.stripe.com/v3/buy-button.js";
        s.onload = () => resolve();
        document.body.appendChild(s);
      });

    const waitForCustomElement = () =>
      new Promise((resolve) => {
        const ready = () =>
          typeof window !== "undefined" &&
          window.customElements &&
          window.customElements.get("stripe-buy-button");
        if (ready()) return resolve();
        const t = setInterval(() => {
          if (ready()) {
            clearInterval(t);
            resolve();
          }
        }, 50);
      });

    const mountButton = (container, buyButtonId) => {
      if (!container) return;
      // Clean up first to avoid StrictMode double-mount duplicates
      container.innerHTML = "";
      const el = document.createElement("stripe-buy-button");
      el.setAttribute("buy-button-id", buyButtonId);
      el.setAttribute(
        "publishable-key",
        "pk_live_51OqwYREqx1V56brjlfCmzkffqzBWBBgyREU6ur6n5Y6p15LsbSkYKJUpn0xhW48ggEZwrGJ4lWEX8pC0Mtu5fImj00neIwVyEw"
      );
      container.appendChild(el);
    };

    let cancelled = false;

    (async () => {
      await ensureScript();
      await waitForCustomElement();
      if (cancelled) return;
      // SAME IDs as your snippet:
      mountButton(dayBtnRef.current, "buy_btn_1RxVUeEqx1V56brjhmKQ0BPB"); // £40/day
      mountButton(weekBtnRef.current, "buy_btn_1RxWLgEqx1V56brjoWSizypI"); // full week
    })();

    return () => {
      cancelled = true;
      if (dayBtnRef.current) dayBtnRef.current.innerHTML = "";
      if (weekBtnRef.current) weekBtnRef.current.innerHTML = "";
    };
  }, []);

  // ---- Styles
  const box = {
    background: "#0f0f0f",
    border: "1px solid #ff7a18",
    borderRadius: 12,
    color: "#e6e6e6",
    padding: 16,
    maxWidth: 1200,
    margin: "0 auto 24px auto",
  };
  const heroBar = {
    background:
      "linear-gradient(90deg, rgba(255,122,24,0.18), rgba(255,122,24,0.05))",
    border: "1px solid #ff7a18",
    borderRadius: 10,
    padding: "10px 14px",
    color: "#ffd4b0",
    fontWeight: 700,
    textAlign: "center",
    marginBottom: 10,
  };
  const title = {
    color: "#eaeaea",
    fontSize: "1.7rem",
    fontWeight: 900,
    textAlign: "center",
    margin: "6px 0 8px",
  };
  const subtitle = {
    color: "#d9d9d9",
    fontSize: "1.08rem",
    textAlign: "center",
    marginBottom: 16,
  };
  const sectionTitle = {
    fontSize: "1.1rem",
    fontWeight: 800,
    margin: "16px 0 8px",
    color: "#ffffff",
  };
  const desc = { fontSize: "1rem", lineHeight: 1.6, color: "#d2d2d2" };
  const list = { margin: "10px 0 14px 18px" };
  const imgStyle = {
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: 8,
  };
  const stripeWrap = {
    display: "flex",
    justifyContent: "center",
    marginTop: 18,
  };
  const ctaBtn = {
    display: "inline-block",
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #2d6a4f",
    background: "#2d6a4f",
    color: "#ffffff",
    textDecoration: "none",
    fontWeight: 700,
    transition: "transform .05s ease",
  };
  const ctaOutline = {
    ...ctaBtn,
    background: "transparent",
    border: "1px solid #2d6a4f",
    color: "#b7f7d8",
  };

  // ---- Map (no API key)
  const MAP_EMBED_SRC =
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d20066.94372986801!2d-1.3774550256836187!3d51.04629310000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48740d0a0077de65%3A0x7ee7b2c47c429539!2sBadger%20Farm%20Community%20Centre!5e0!3m2!1sen!2suk!4v1755680316469!5m2!1sen!2suk";

  const VENUE =
    "Badger Farm Community Centre, Badger Farm Road, Winchester SO22 4QB, UK";
  const mapsPlaceUrl =
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(VENUE);
  const mapsDirectionsUrl =
    "https://www.google.com/maps/dir/?api=1&destination=" +
    encodeURIComponent(VENUE);

  const mapWrap = {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%", // 16:9
    borderRadius: 12,
    overflow: "hidden",
    background: "#151515",
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
  };
  const mapIframe = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    border: 0,
  };
  const linkBtn = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #3a3a3a",
    textDecoration: "none",
    color: "#e6e6e6",
    display: "inline-block",
  };

  return (
    <div style={box}>
      <Container fluid className="p-0">
        {/* >>> Boost for the tournament — top emphasis <<< */}
        <div style={heroBar}>
          Boost your child’s tournament result — focused prep for the Hampshire
          Junior Open (LJCC Qualifier) on 16 Nov
        </div>

        <h2 style={title}>Chess Camp – 27–31 October</h2>
        <div style={subtitle}>
          10:00 AM – 2:00 PM • £40/day or £35/day for the full week (£175)
        </div>

        <Row className="g-3 align-items-start">
          {/* Image */}
          <Col xs={12} md={5} className="order-1 order-md-2">
            <img src="/chesscamp.webp" alt="Chess Camp" style={imgStyle} />
          </Col>

          {/* Description + Stripe */}
          <Col xs={12} md={7} className="order-2 order-md-1">
            <div style={desc}>
              <p>
                Our half-term <strong>Chess Camp</strong> (27–31 Oct) is built
                to <strong>boost performance at the upcoming tournament</strong>
                . We welcome <strong>all levels</strong> — from absolute
                beginners to players already preparing for competition.
              </p>
              <ul style={list}>
                <li>
                  <strong>Beginners:</strong> rules, check & checkmate basics,
                  piece coordination, simple tactics.
                </li>
                <li>
                  <strong>Improvers:</strong> tactical motifs, core endgames,
                  opening principles, calculation drills.
                </li>
                <li>
                  <strong>Tournament prep:</strong> decision
                  checklistFFFFFFFFFFF, time management, typical rapidplay
                  positions, mini-tournaments + game analysis.
                </li>
              </ul>
              <p>
                Every day (10:00–14:00): themed lessons, exercises, guided games
                and friendly analysis to build confidence and consistency.
                Choose <strong>daily (£40)</strong> or{" "}
                <strong>full week (£175)</strong>. Limited places.
              </p>
            </div>

            {/* Stripe Buy Buttons (mounted after script is ready) */}
            <Row className="g-3 mt-2">
              <Col xs={12} md={6} className="d-flex justify-content-center">
                <div ref={dayBtnRef} style={stripeWrap} />
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-center">
                <div ref={weekBtnRef} style={stripeWrap} />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* === Tournament CTA (Eventbrite) === */}
        <Row className="g-3 mt-4">
          <Col xs={12}>
            <div
              style={{
                border: "1px solid #244f3d",
                background:
                  "linear-gradient(90deg, rgba(45,106,79,0.20), rgba(45,106,79,0.05))",
                borderRadius: 12,
                padding: 14,
              }}
            >
              <div style={sectionTitle}>Enter the Tournament (16 Nov)</div>
              <p style={{ margin: "0 0 10px", color: "#cdeadc" }}>
                Hampshire Junior Open Chess Congress 2025 — LJCC Qualifier,
                Badger Farm Community Centre (arrive 09:30). Time control: 20′ +
                10″.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <a
                  href={EVENTBRITE_URL}
                  target="_blank"
                  rel="noreferrer"
                  style={ctaBtn}
                >
                  Enter Tournament (Eventbrite)
                </a>
                <a
                  href={EVENTBRITE_URL}
                  target="_blank"
                  rel="noreferrer"
                  style={ctaOutline}
                  title="Open Eventbrite in a new tab"
                >
                  View Details
                </a>
              </div>
            </div>
          </Col>
        </Row>

        {/* === Location + Map (BOTTOM) === */}
        <Row className="g-3 mt-4">
          <Col xs={12}>
            <h3 style={{ fontSize: "1.25rem", margin: "0 0 8px" }}>
              Where to find us
            </h3>
          </Col>

          {/* Address */}
          <Col xs={12} md={5}>
            <div
              style={{
                border: "1px solid #2a2a2a",
                borderRadius: 12,
                padding: 12,
                background: "#151515",
              }}
            >
              <p style={{ margin: 0, fontWeight: 700 }}>
                Badger Farm & Oliver’s Battery Residents’ Community Association
              </p>
              <p style={{ margin: "6px 0 0 0" }}>
                Badger Farm Community Centre
              </p>
              <p style={{ margin: "6px 0 0 0" }}>Badger Farm Road</p>
              <p style={{ margin: "6px 0 0 0" }}>Winchester</p>
              <p style={{ margin: "6px 0 0 0" }}>SO22 4QB</p>

              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <a
                  href={mapsPlaceUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={linkBtn}
                >
                  View larger map
                </a>
                <a
                  href={mapsDirectionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={linkBtn}
                >
                  Directions
                </a>
              </div>
            </div>
          </Col>

          {/* Map */}
          <Col xs={12} md={7}>
            <div style={mapWrap}>
              <iframe
                title="Map: Badger Farm Community Centre"
                src={MAP_EMBED_SRC}
                style={mapIframe}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
